import { Play } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { JobConfig } from "@/api/types";

interface TriggerModalProps {
  job: JobConfig | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires synchronously when the user clicks "Run now". The parent
   *  closes the modal AND opens the live sheet AND fires the actual
   *  POST. Keeping the network call out of the modal lets the sheet
   *  render its "Triggering…" placeholder before any latency, so a
   *  job that finishes in 2 s still feels alive. */
  onConfirm: (job: JobConfig) => void;
}

export function TriggerModal({
  job,
  open,
  onOpenChange,
  onConfirm,
}: TriggerModalProps) {
  function confirm() {
    if (!job) return;
    onConfirm(job);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100">
            <Play className="h-4 w-4 text-indigo-600" />
          </div>
          <DialogTitle>Run this job now?</DialogTitle>
          <DialogDescription className="leading-relaxed">
            {job ? (
              <>
                <span className="font-medium text-slate-700">{job.name}</span>{" "}
                will start immediately. The next scheduled run is unaffected.
              </>
            ) : (
              "The job will start immediately."
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={confirm} variant="primary">
            Run now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
